import { SimulationResult, UserInfo } from "../types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from "recharts";
import { useState } from "react";

export type SimulationResultProps = {
    userInfo: UserInfo
    simulationResult: SimulationResult | null
}

export const SimulationResultComponent: React.FC<SimulationResultProps> = ({ userInfo, simulationResult }) => {
    const [isPortfolioLogarithmic, setIsPortfolioLogarithmic] = useState(false);
    const [isWithdrawalsLogarithmic, setIsWithdrawalsLogarithmic] = useState(false);
    
    if (!simulationResult) {
        return <div className="text-center p-4">No simulation results to display yet.</div>;
    }

    // Create data array for the chart
    const chartData = simulationResult.years.map((year, index) => ({
        age: userInfo.currentAge + year,
        portfolioValue: Math.max(1, simulationResult.portfolioValues[index]), // Ensure positive values for log scale
        withdrawals: simulationResult.withdrawals[index] == 0 ? undefined : simulationResult.withdrawals[index]  // Ensure positive values for log scale
    }));

    const withdrawalData =  chartData // chartData.filter(data => data.age >= userInfo.retirementAge);

    // Format currency values for display
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(value);
    };

    return (
        <div className="space-y-4 mt-6">
            <h2 className="text-lg font-semibold">Portfolio Projection</h2>
            
            <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="flex justify-between mb-4">
                </div>

                {/* Portfolio Value Chart */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-md font-medium">Portfolio Value</h3>
                        <div className="flex items-center space-x-2">
                            <span className="text-sm">Linear</span>
                            <button 
                                className={`relative inline-flex h-6 w-11 items-center rounded-full ${isPortfolioLogarithmic ? 'bg-blue-600' : 'bg-gray-200'}`}
                                onClick={() => setIsPortfolioLogarithmic(!isPortfolioLogarithmic)}
                            >
                                <span 
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${isPortfolioLogarithmic ? 'translate-x-6' : 'translate-x-1'}`} 
                                />
                            </button>
                            <span className="text-sm">Log</span>
                        </div>
                    </div>
                    <div className="h-60 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 50, bottom: 10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="age" 
                                    label={{ value: 'Age', position: 'insideBottomRight', offset: 0 }} 
                                />
                                <YAxis 
                                    scale={isPortfolioLogarithmic ? 'log' : 'auto'}
                                    domain={isPortfolioLogarithmic ? [(dataMin: number) => Math.max(1, dataMin), 'auto'] : [0, 'auto']}
                                    allowDataOverflow={isPortfolioLogarithmic}
                                    tickFormatter={(value) => formatCurrency(value)}
                                    label={{
                                        // value: `Portfolio Value ${isPortfolioLogarithmic ? '(log scale)' : ''}`, 
                                        angle: -90, 
                                        position: 'insideLeft'
                                    }}
                                />
                                <Tooltip 
                                    formatter={(value: number, name: string) => {
                                        // Don't format values of 1 that were used as placeholders for log scale
                                        return value === 1 ? ['$0', name] : [formatCurrency(value), name];
                                    }}
                                    labelFormatter={(label) => `Age: ${label}`}
                                />
                                <ReferenceLine 
                                    x={userInfo.retirementAge} 
                                    stroke="#ff0000" 
                                    strokeDasharray="5 5"
                                    label={{ 
                                        value: 'Retirement Age', 
                                        position: 'top', 
                                        fill: '#ff0000',
                                        fontSize: 12
                                    }} 
                                />

                                <Line 
                                    type="monotone" 
                                    dataKey="portfolioValue" 
                                    stroke="#8884d8" 
                                    activeDot={{ r: 8 }} 
                                    name="Portfolio Value"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                
                {/* Withdrawal Chart */}
                <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-md font-medium">Annual Withdrawals</h3>
                        <div className="flex items-center space-x-2">
                            <span className="text-sm">Linear</span>
                            <button 
                                className={`relative inline-flex h-6 w-11 items-center rounded-full ${isWithdrawalsLogarithmic ? 'bg-blue-600' : 'bg-gray-200'}`}
                                onClick={() => setIsWithdrawalsLogarithmic(!isWithdrawalsLogarithmic)}
                            >
                                <span 
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${isWithdrawalsLogarithmic ? 'translate-x-6' : 'translate-x-1'}`} 
                                />
                            </button>
                            <span className="text-sm">Log</span>
                        </div>
                    </div>
                    <div className="h-40 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={withdrawalData}
                                
                                margin={{ top: 10, right: 30, left: 50, bottom: 10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="age" 
                                    min={userInfo.currentAge}
                                    label={{ value: 'Age', position: 'insideBottomRight', offset: 0 }} 
                                />
                                <YAxis 
                                    scale={isWithdrawalsLogarithmic ? 'log' : 'auto'}
                                    domain={isWithdrawalsLogarithmic ? [(dataMin: number) => Math.max(1, dataMin), 'auto'] : [0, 'auto']}
                                    allowDataOverflow={isWithdrawalsLogarithmic}
                                    tickFormatter={(value) => formatCurrency(value)}
                                    label={{
                                        // value: `Annual Withdrawals ${isWithdrawalsLogarithmic ? '(log scale)' : ''}`, 
                                        angle: -90, 
                                        position: 'insideLeft'
                                    }}
                                />
                                <Tooltip 
                                    formatter={(value: number, name: string) => {
                                        return value === 1 ? ['$0', name] : [formatCurrency(value), name];
                                    }}
                                    labelFormatter={(label) => `Age: ${label}`}
                                />
                                <ReferenceLine 
                                    x={userInfo.retirementAge} 
                                    stroke="#ff0000" 
                                    strokeDasharray="5 5"
                                    label={{ 
                                        value: 'Retirement Age', 
                                        position: 'right', 
                                        fill: '#ff0000',
                                        fontSize: 12
                                    }} 
                                />
                                <ReferenceLine
                                    y={userInfo.annualExpenses}
                                    stroke="#ff0000"
                                    strokeDasharray="5 5" 
                                    label={{ 
                                        value: 'Expenses', 
                                        position: 'top', 
                                        fill: '#ff0000',
                                        fontSize: 12
                                    }} />
                                <Line 
                                    type="monotone" 
                                    dataKey="withdrawals" 
                                    stroke="#82ca9d" 
                                    activeDot={{ r: 6 }} 
                                    name="Annual Withdrawals"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="flex justify-between mt-4">
                    <div>
                        <p className="text-sm text-gray-500">Worst Case Ending Value</p>
                        <p className="text-lg font-medium">{formatCurrency(simulationResult.worstCaseEndingValue)}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Median Ending Value</p>
                        <p className="text-lg font-medium">{formatCurrency(simulationResult.medianEndingValue)}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Best Case Ending Value</p>
                        <p className="text-lg font-medium">{formatCurrency(simulationResult.bestCaseEndingValue)}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}